<template>
  <!--
    Labelled by reference, not by `for`: the field is a group of spinbutton
    segments, and a group cannot be a `<label for>` target.
  -->
  <DSDateField
    v-if="field.control === 'date'"
    :id
    :model-value="text"
    size="sm"
    :aria-labelledby="labelledBy"
    :aria-label
    @update:model-value="emit('change', $event ?? '')"
  />

  <!--
    An amount, through the shared money field: locale-aware parsing, grouped
    display, no wheel-rewrites and — the reason it is shared rather than
    configured here — no step snapping, which is what used to round a typed
    1,000,000.55 to 1,000,001. See DSAmountField.

    Unbounded, deliberately. The field collects a magnitude — "how much did
    you pay" — and the sign is applied where the record is written:
    `pasteAmount` reads the repository's rule for that entry and returns
    `-Math.abs`/`Math.abs` of what it was given, which is the same convention
    the transaction form has always worked to.

    Handing that rule to the control instead, as `min`/`max` from
    `amountBoundsFor`, destroyed the value: Reka *clamps* to its bounds rather
    than refusing them, so under `add position`'s `maxzero` a typed 500,000
    was committed as 0 on blur — and only once the transaction settings had
    loaded, so the same keystrokes worked or didn't depending on the timing.
  -->
  <DSAmountField
    v-else-if="field.control === 'number'"
    :id
    :model-value="typeof value === 'number' ? value : undefined"
    :placeholder
    :aria-label
    @update:model-value="emit('change', $event)"
    @blur="emit('blur')"
  />

  <!--
    A select you can type into, which can also carry one suggested row —
    pinned, marked, with its grounds, and exempt from the search. Reka does the
    matching itself; see DSSuggestionCombobox on why the pinning cannot.
  -->
  <DSSuggestionCombobox
    v-else-if="field.control === 'combobox'"
    :id
    :model-value="text"
    :options="comboboxOptions"
    :suggestion
    :loading="suggestionLoading"
    loading-label="Working out the right type…"
    :askable
    :placeholder
    :aria-label
    @update:model-value="emit('change', String($event ?? ''))"
    @blur="emit('blur')"
    @ask="emit('ask')"
  />

  <DSSelect
    v-else-if="field.control === 'select'"
    :model-value="text"
    @update:model-value="emit('change', String($event))"
  >
    <DSSelectTrigger
      :id
      :aria-label
    >
      <DSSelectValue :placeholder />
    </DSSelectTrigger>
    <DSSelectContent>
      <DSSelectItem
        v-for="option in options"
        :key="option"
        :value="option"
      >
        {{ option }}
      </DSSelectItem>
    </DSSelectContent>
  </DSSelect>

  <DSInput
    v-else
    :id
    :model-value="text"
    type="text"
    :placeholder
    :aria-label
    @update:model-value="emit('change', String($event))"
    @blur="emit('blur')"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  DSAmountField,
  DSDateField,
  DSInput,
  DSSelect,
  DSSelectContent,
  DSSelectItem,
  DSSelectTrigger,
  DSSelectValue,
  DSSuggestionCombobox,
  type ComboboxSuggestion,
  type SuggestionComboboxOption,
} from '@ds'

import { useWizardOptions } from '../utils/options'
import { WizardFieldDef } from '../utils/types'

/*
 * One control, chosen by the field's declared type. Kept apart from WizardField
 * so the branch lives in one place — a heading field and a body field render
 * the same controls, they just sit in different slots.
 */
const { field, value, id, labelledBy } = defineProps<{
  field: WizardFieldDef
  /**
   * The field's current value, straight off the form: a string for every
   * control but `number`, which holds a number or `undefined`. Each branch
   * below reads the shape its own control speaks — see utils/schema.
   */
  value: string | number | undefined
  /**
   * The control's `id`, so the visible caption can be its `<label for>`. When
   * it is absent the caption is not on screen, and the control falls back to
   * naming itself with `aria-label`.
   */
  id?: string
  /**
   * `id` of the element that labels this control, for the one control that
   * cannot be a `<label for>` target — the date field's segment group.
   */
  labelledBy?: string
  /**
   * A row to pin above a combobox's options, with the grounds for suggesting
   * it. Only a `combobox` control can show one.
   *
   * A text field takes no suggestion: a name the assistant derived is written
   * straight into the field and marked READ, the same as a value the sentence
   * stated. An accept row under the input asked the user to agree to something
   * before they could see it in place, which is a step for nothing.
   */
  suggestion?: ComboboxSuggestion
  /** That row, still being decided. Same control, same restriction. */
  suggestionLoading?: boolean
  /** Offers the "Ask AI" row in place of a suggestion — combobox only. */
  askable?: boolean
}>()

const emit = defineEmits<{
  (e: 'change', value: string | number | undefined): void
  (e: 'blur'): void
  /** The "Ask AI" row was clicked — combobox only. */
  (e: 'ask'): void
}>()

/*
 * Named twice would be named wrong: with a `<label for>` on screen the control
 * already has its accessible name, and an aria-label here would override the
 * very text the user can see.
 */
const ariaLabel = computed(() => (id || labelledBy ? undefined : field.label))

const { optionsFor } = useWizardOptions()

/*
 * Every other control speaks strings, and the form holds strings for them.
 * `?? ''` rather than a cast: a field the wizard has not seeded yet is
 * undefined, and an input bound to undefined is an uncontrolled one.
 */
const text = computed(() => String(value ?? ''))

/** A carried list wins over a named source — see WizardFieldDef.optionsList. */
const options = computed(() => field.optionsList ?? optionsFor(field.options))

/*
 * The definitions carry plain strings, so the value is the label. The richer
 * shape exists for rows that need an aside — an account's type beside its name —
 * which the account picker supplies for itself.
 */
const comboboxOptions = computed<SuggestionComboboxOption[]>(() =>
  options.value.map(option => ({ value: option, label: option })),
)

/*
 * Its own placeholder, or an em dash to mark it as skippable.
 *
 * A defaulted field used to name its default here — "USD · repository default"
 * — as a promise of what leaving it alone would do. The wizard now seeds those
 * fields with the value itself (see resolveDefaults), so the promise had
 * nothing left to describe: the placeholder was reachable only when the
 * repository had no currency to offer, which is exactly when the sentence was
 * untrue.
 */
const placeholder = computed(() => {
  if (field.placeholder) return field.placeholder
  return field.control === 'select' ? 'Choose one' : '—'
})
</script>
